import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRequest } from 'ahooks';
import { useStyles } from './list.style';
import { getColumns, getFilterConfig } from './util/list.util';
import { RequestData } from 'src/app/common/components/ProTable';
import { FilterState } from 'src/app/common/components/ProTable/ProFilter';
import { CampaignItem } from '../../../types/campaign-types';
import { RouteComponentProps } from 'react-router-dom';
import { getCampaignList, getCampaignTypeList, getFilterCampaignTypeList } from './util/api.util';
import { useTranslation } from 'src/app/common/hooks/translation.hook';
import { AGENCY_CAMPAIGN_LIST_PATH } from '../../../constants';
import { UnreadCampaignMap } from 'src/app/common/components/layout/components/aside/components/nested-menu';
import { updateMenuBadge } from 'src/redux/common/commonSlice';

export type ListPageProps = {
  history: RouteComponentProps['history'];
  cacheParams?: React.MutableRefObject<Record<string, any> | undefined>;
};

export const useList = ({ cacheParams, history }: ListPageProps) => {
  const { t: Translation } = useTranslation();
  const { menuBadge } = useSelector((state: any) => state.common);

  const { classes } = useStyles();
  const styles = useMemo(() => classes, [classes]);
  const [keyword, setKeyword] = useState(cacheParams?.current?.filterState?.keyword ?? '');
  const [createModalVisible, handleModalVisible] = useState<boolean>(false);

  const dispatch = useDispatch();

  const { data: createCampaignType } = useRequest(async () => getCampaignTypeList(dispatch), { manual: false });

  const { data: campaignType } = useRequest(async () => getFilterCampaignTypeList(dispatch), { manual: false });

  const columns = useMemo(() => getColumns({ t: Translation, keyword, styles }), [Translation, keyword, styles]);

  const filterConfig = useMemo(() => getFilterConfig(Translation, campaignType), [campaignType, Translation]);

  const getData = useCallback(
    async (
      params: { page: number; pageSize: number; current?: number | undefined; keyword?: string | undefined },
      sort?: { [key: string]: any },
      filter?: { [key: string]: any },
    ): Promise<RequestData<CampaignItem>> => getCampaignList(dispatch, params, sort, filter),
    [dispatch],
  );

  const onFilterChange = useCallback(
    (params: FilterState) => {
      const { keyword } = params.filterState;
      cacheParams && (cacheParams.current = params);
      setKeyword(keyword);
    },
    [cacheParams],
  );

  const onTypeSelected = (data: any) => {
    handleModalVisible(false);
    history.push(`${AGENCY_CAMPAIGN_LIST_PATH}/create/${data}`);
  };

  const onClickRow = (row: CampaignItem) => {
    history.push(`${AGENCY_CAMPAIGN_LIST_PATH}/detail/${row.campaignId}`);
    console.log('row', row);

    !row.lastViewedTime &&
      menuBadge[UnreadCampaignMap.AGENCY_CAMPAIGN] &&
      dispatch(
        updateMenuBadge({
          ...menuBadge,
          [UnreadCampaignMap.AGENCY_CAMPAIGN]: menuBadge[UnreadCampaignMap.AGENCY_CAMPAIGN] - 1,
        }),
      );
  };

  return {
    Translation,
    filterConfig,
    cacheParams: cacheParams?.current,
    onFilterChange,
    columns,
    getData,
    createModalVisible,
    handleModalVisible,
    campaignTypeList: createCampaignType,
    onTypeSelected,
    onClickRow,
  };
};
